import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { comics } from '@/mocks/comics';
import { useHistory } from '@/hooks/useHistory';

export default function ReadPage() {
  const { comicId = '', chapterId = '' } = useParams();
  const navigate = useNavigate();
  const { addHistory } = useHistory();

  const comic = comics.find((item) => item.id === comicId);
  const chapterIndex = comic ? comic.chapters.findIndex((item) => item.id === chapterId) : -1;
  const chapter = comic && chapterIndex >= 0 ? comic.chapters[chapterIndex] : undefined;

  const [theme, setTheme] = useState<'day' | 'night'>('day');
  const [fontScale, setFontScale] = useState(1);
  const [dirOpen, setDirOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  useEffect(() => {
    if (comic && chapter) {
      addHistory({ comicId: comic.id, chapterId: chapter.id, chapterTitle: chapter.title });
    }
  }, [comic, chapter, addHistory]);

  const prev = useMemo(() => (comic && chapterIndex > 0 ? comic.chapters[chapterIndex - 1] : null), [comic, chapterIndex]);
  const next = useMemo(
    () => (comic && chapterIndex >= 0 && chapterIndex < comic.chapters.length - 1 ? comic.chapters[chapterIndex + 1] : null),
    [comic, chapterIndex],
  );

  const goChapter = (id: string) => {
    setDirOpen(false);
    navigate(`/read/${comicId}/${id}`);
  };

  if (!comic || !chapter) {
    return (
      <div className="mx-auto flex max-w-[1280px] flex-col items-center justify-center px-4 py-24 text-center">
        <span className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-3xl text-foreground-400">
          <i className="ri-book-open-line" />
        </span>
        <p className="text-foreground-700">章节不存在或已下架</p>
        <Link
          to="/list"
          className="mt-5 whitespace-nowrap rounded-full bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition hover:bg-primary-600"
        >
          返回漫画库
        </Link>
      </div>
    );
  }

  const isNight = theme === 'night';

  return (
    <div className={isNight ? 'bg-foreground-950' : 'bg-background-100'}>
      <div className={isNight ? 'text-background-100' : 'text-foreground-900'}>
        <div
          className={`sticky top-16 z-40 flex items-center gap-3 border-b px-4 py-3 md:px-8 ${
            isNight ? 'border-foreground-800 bg-foreground-900/95' : 'border-background-200 bg-background-50/95'
          } backdrop-blur`}
        >
          <button
            type="button"
            onClick={() => navigate(`/detail/${comic.id}`)}
            className="flex items-center gap-1.5 whitespace-nowrap text-sm transition hover:text-primary-500 cursor-pointer"
          >
            <i className="ri-arrow-left-line" />
            <span className="hidden sm:inline">返回详情</span>
          </button>

          <div className="min-w-0 flex-1 text-center">
            <p className="truncate text-sm font-medium">{comic.title}</p>
            <p className={`truncate text-xs ${isNight ? 'text-background-300' : 'text-foreground-500'}`}>
              {chapter.title}
            </p>
          </div>

          <button
            type="button"
            onClick={() => setDirOpen(true)}
            className="flex items-center gap-1.5 whitespace-nowrap rounded-full border px-3 py-1.5 text-xs transition hover:border-primary-400 hover:text-primary-500 cursor-pointer border-background-300/70"
          >
            <i className="ri-list-check-2" />
            目录
          </button>
          <button
            type="button"
            onClick={() => setSettingsOpen((prevState) => !prevState)}
            className="flex items-center gap-1.5 whitespace-nowrap rounded-full border px-3 py-1.5 text-xs transition hover:border-primary-400 hover:text-primary-500 cursor-pointer border-background-300/70"
          >
            <i className="ri-settings-3-line" />
            设置
          </button>
        </div>

        {settingsOpen && (
          <div
            className={`sticky top-[124px] z-30 flex flex-wrap items-center gap-4 border-b px-4 py-3 text-sm md:px-8 ${
              isNight ? 'border-foreground-800 bg-foreground-900' : 'border-background-200 bg-background-50'
            }`}
          >
            <div className="flex items-center gap-2">
              <span className="text-xs">字号</span>
              <button
                type="button"
                onClick={() => setFontScale((value) => Math.max(0.8, Number((value - 0.1).toFixed(1))))}
                className="flex h-8 w-8 items-center justify-center rounded-full border border-background-300/70 transition hover:border-primary-400 cursor-pointer"
              >
                <i className="ri-subtract-line" />
              </button>
              <span className="w-10 text-center text-xs">{Math.round(fontScale * 100)}%</span>
              <button
                type="button"
                onClick={() => setFontScale((value) => Math.min(1.8, Number((value + 0.1).toFixed(1))))}
                className="flex h-8 w-8 items-center justify-center rounded-full border border-background-300/70 transition hover:border-primary-400 cursor-pointer"
              >
                <i className="ri-add-line" />
              </button>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs">背景</span>
              <div className="flex rounded-full bg-background-200 p-0.5">
                <button
                  type="button"
                  onClick={() => setTheme('day')}
                  className={`flex items-center gap-1 rounded-full px-3 py-1 text-xs transition cursor-pointer ${
                    !isNight ? 'bg-background-50 text-foreground-900' : 'text-foreground-600'
                  }`}
                >
                  <i className="ri-sun-line" />
                  白天
                </button>
                <button
                  type="button"
                  onClick={() => setTheme('night')}
                  className={`flex items-center gap-1 rounded-full px-3 py-1 text-xs transition cursor-pointer ${
                    isNight ? 'bg-foreground-800 text-background-50' : 'text-foreground-600'
                  }`}
                >
                  <i className="ri-moon-line" />
                  夜晚
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="mx-auto flex max-w-[820px] flex-col items-center px-2 py-6 md:px-4">
          <h1 className="mb-4 text-center font-heading" style={{ fontSize: `${1.25 * fontScale}rem` }}>
            {chapter.title}
          </h1>
          <div className="w-full space-y-2">
            {chapter.pages.map((page, index) => (
              <div key={`${chapter.id}-${index}`} className="w-full overflow-hidden rounded-md bg-background-200">
                <img
                  src={page}
                  alt={`${comic.title} ${chapter.title} 第 ${index + 1} 页`}
                  className="w-full object-contain"
                />
              </div>
            ))}
          </div>
          <p className={`mt-6 text-xs ${isNight ? 'text-background-300' : 'text-foreground-500'}`}>
            本话内容已展示完毕（演示图片）
          </p>
        </div>

        <div
          className={`sticky bottom-0 z-40 flex items-center gap-3 border-t px-4 py-3 md:px-8 ${
            isNight ? 'border-foreground-800 bg-foreground-900/95' : 'border-background-200 bg-background-50/95'
          } backdrop-blur`}
        >
          <button
            type="button"
            disabled={!prev}
            onClick={() => prev && goChapter(prev.id)}
            className="flex flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-full border border-background-300/70 py-2.5 text-sm transition enabled:hover:border-primary-400 enabled:hover:text-primary-500 disabled:cursor-not-allowed disabled:opacity-40 cursor-pointer"
          >
            <i className="ri-arrow-left-s-line" />
            上一章
          </button>
          <button
            type="button"
            onClick={() => setDirOpen(true)}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-background-300/70 transition hover:border-primary-400 cursor-pointer"
          >
            <i className="ri-menu-2-line" />
          </button>
          <button
            type="button"
            disabled={!next}
            onClick={() => next && goChapter(next.id)}
            className="flex flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-full bg-primary-500 py-2.5 text-sm font-medium text-background-50 transition enabled:hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-40 cursor-pointer"
          >
            下一章
            <i className="ri-arrow-right-s-line" />
          </button>
        </div>
      </div>

      {dirOpen && (
        <div className="fixed inset-0 z-[70]">
          <div className="absolute inset-0 bg-foreground-950/50" onClick={() => setDirOpen(false)} />
          <div className="absolute right-0 top-0 h-full w-[320px] max-w-[85vw] overflow-y-auto bg-background-50 p-4">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-foreground-900">{comic.title}</p>
                <p className="text-xs text-foreground-500">共 {comic.chapters.length} 话</p>
              </div>
              <button
                type="button"
                onClick={() => setDirOpen(false)}
                className="flex h-8 w-8 items-center justify-center rounded-full bg-background-100 text-foreground-700 cursor-pointer"
              >
                <i className="ri-close-line" />
              </button>
            </div>
            <div className="space-y-1.5">
              {comic.chapters.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => goChapter(item.id)}
                  className={`w-full truncate rounded-md px-3 py-2.5 text-left text-sm transition cursor-pointer ${
                    item.id === chapter.id
                      ? 'bg-primary-500 font-medium text-background-50'
                      : 'bg-background-100 text-foreground-700 hover:bg-primary-50 hover:text-primary-600'
                  }`}
                >
                  {item.title}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}